import React, { useEffect, ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { useWizard } from '../contexts/WizardContext';

export interface WizardStepProps {
  stepIndex: number;
  title?: string;
  description?: string;
  children: ReactNode;
  validate?: () => boolean | Promise<boolean>;
  onEnter?: () => void | Promise<void>;
  onExit?: () => void | Promise<void>;
}

export const WizardStep: React.FC<WizardStepProps> = ({
  stepIndex,
  title,
  description,
  children,
  validate,
  onEnter,
  onExit
}) => {
  const wizard = useWizard();
  const isActive = wizard.activeStep === stepIndex;
  const prevActiveStep = React.useRef(wizard.activeStep);

  useEffect(() => {
    // Call onEnter when step becomes active
    if (isActive && prevActiveStep.current !== stepIndex && onEnter) {
      onEnter();
    }

    // Call onExit when step becomes inactive
    if (!isActive && prevActiveStep.current === stepIndex && onExit) {
      onExit();
    }

    prevActiveStep.current = wizard.activeStep;
  }, [isActive, stepIndex, onEnter, onExit]);

  useEffect(() => {
    // Run validation when active
    if (isActive && validate) {
      const runValidation = async () => {
        try {
          const isValid = await validate();
          wizard.setStepValidation(stepIndex, isValid);
        } catch (err) {
          console.error('Validation error:', err);
          wizard.setStepValidation(stepIndex, false);
        }
      };
      runValidation();
    }
  }, [isActive, validate, stepIndex, wizard.data]); // Re-run when wizard data changes

  if (!isActive) return null;

  return (
    <Box>
      {title && (
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
      )}
      {description && (
        <Typography variant="body2" color="text.secondary" paragraph>
          {description}
        </Typography>
      )}
      {children}
    </Box>
  );
};
